/* Elzari behavior helpers, vanilla, follows the theme's custom-element idiom.
   Motion specs from docs/design-spec.md §1 (Motion). */

(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Scroll reveal ---------- */
  const revealNow = (el) => el.setAttribute('data-elz-on', '1');
  let revealObserver = null;

  function initReveal(root) {
    const targets = (root || document).querySelectorAll('[data-elz-reveal]:not([data-elz-on])');
    if (!targets.length) return;
    if (reducedMotion.matches || window.Shopify?.designMode) {
      targets.forEach(revealNow);
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              revealNow(entry.target);
              revealObserver.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.08 }
      );
    }
    targets.forEach((el) => revealObserver.observe(el));
  }

  document.addEventListener('DOMContentLoaded', () => initReveal());
  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));

  /* ---------- Rotating announcement ticker ---------- */
  class ElzTicker extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-elz-ticker-track]');
      this.items = Array.from(this.querySelectorAll('[data-elz-ticker-item]'));
      this.items.forEach((el) => (el.hidden = false));
      if (!this.track || this.items.length < 2) return;
      this.index = 0;
      // a swipe/drag pauses the auto-advance, it resumes after a beat
      ['pointerdown', 'touchstart', 'wheel'].forEach((type) =>
        this.track.addEventListener(type, () => this.pauseBriefly(), { passive: true })
      );
      this.track.addEventListener(
        'scroll',
        () => {
          if (this.raf) return;
          this.raf = requestAnimationFrame(() => {
            this.raf = null;
            this.index = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
          });
        },
        { passive: true }
      );
      if (!reducedMotion.matches) this.start();
    }
    start() {
      clearInterval(this.interval);
      this.interval = setInterval(() => this.step(), 3000);
    }
    pauseBriefly() {
      clearInterval(this.interval);
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => {
        if (!reducedMotion.matches) this.start();
      }, 6000);
    }
    step() {
      this.index = (this.index + 1) % this.items.length;
      this.track.scrollTo({ left: this.index * this.track.clientWidth, behavior: 'smooth' });
    }
    disconnectedCallback() {
      clearInterval(this.interval);
      clearTimeout(this.resumeTimer);
    }
  }
  if (!customElements.get('elz-ticker')) customElements.define('elz-ticker', ElzTicker);

  /* ---------- Marquee (duplicates its track for a seamless loop) ---------- */
  class ElzMarquee extends HTMLElement {
    connectedCallback() {
      const track = this.querySelector('.elz-marquee__track');
      if (!track || track.dataset.cloned) return;
      track.insertAdjacentHTML('beforeend', track.innerHTML);
      track.dataset.cloned = '1';
      track.setAttribute('aria-hidden', 'false');
    }
  }
  if (!customElements.get('elz-marquee')) customElements.define('elz-marquee', ElzMarquee);

  /* ---------- Scroll-snap carousel with prev/next buttons ---------- */
  class ElzCarousel extends HTMLElement {
    connectedCallback() {
      this.scroller = this.querySelector('[data-elz-scroller]');
      this.prevBtns = Array.from(this.querySelectorAll('[data-elz-prev]'));
      this.nextBtns = Array.from(this.querySelectorAll('[data-elz-next]'));
      this.prevBtns.forEach((b) => b.addEventListener('click', () => this.scrollByDir(-1)));
      this.nextBtns.forEach((b) => b.addEventListener('click', () => this.scrollByDir(1)));
      if (this.scroller) {
        this.scroller.addEventListener('scroll', () => this.queueArrowUpdate(), { passive: true });
        if ('ResizeObserver' in window) {
          new ResizeObserver(() => this.queueArrowUpdate()).observe(this.scroller);
        }
        this.updateArrows();
      }
    }
    scrollByDir(dir) {
      if (!this.scroller) return;
      this.scroller.scrollBy({
        left: dir * Math.min(640, this.scroller.clientWidth * 0.9),
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    }
    queueArrowUpdate() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this.updateArrows();
      });
    }
    updateArrows() {
      const s = this.scroller;
      if (!s) return;
      const max = s.scrollWidth - s.clientWidth;
      const atStart = s.scrollLeft <= 2;
      const atEnd = s.scrollLeft >= max - 2;
      this.prevBtns.forEach((b) => {
        b.disabled = atStart;
        b.classList.toggle('elz-iconbtn--solid', !atStart);
      });
      this.nextBtns.forEach((b) => {
        b.disabled = atEnd;
        b.classList.toggle('elz-iconbtn--solid', !atEnd);
      });
    }
  }
  if (!customElements.get('elz-carousel')) customElements.define('elz-carousel', ElzCarousel);

  /* ---------- Pill tab switcher ---------- */
  class ElzTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-elz-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-elz-panel]'));
      this.tabs.forEach((tab) => tab.addEventListener('click', () => this.select(tab.dataset.elzTab)));
    }
    select(index) {
      this.tabs.forEach((tab) => {
        const on = tab.dataset.elzTab === index;
        tab.classList.toggle('elz-tabbed__tab--active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.elzPanel !== index;
      });
    }
  }
  if (!customElements.get('elz-tabs')) customElements.define('elz-tabs', ElzTabs);

  /* ---------- Toast ---------- */
  const ElzToast = {
    el: null,
    timer: null,
    show(message, opts = {}) {
      this.hide();
      const el = document.createElement('div');
      el.className = 'elz-toast';
      el.setAttribute('role', 'status');
      const link = opts.link || window.routes?.cart_url || '/cart';
      const linkLabel = opts.linkLabel || el.getAttribute('data-link-label') || 'View cart →';
      el.innerHTML = '<span class="elz-toast__check" aria-hidden="true">✓</span><span></span>';
      el.children[1].textContent = message;
      if (link) {
        const a = document.createElement('a');
        a.href = link;
        a.textContent = linkLabel;
        el.appendChild(a);
      }
      document.body.appendChild(el);
      this.el = el;
      this.timer = setTimeout(() => this.hide(), 2600);
    },
    hide() {
      clearTimeout(this.timer);
      if (this.el) {
        this.el.remove();
        this.el = null;
      }
    },
  };
  window.ElzToast = ElzToast;

  /* ---------- Instant-feel navigation ----------
     Prefetch same-origin pages on first hover/touch (speculation rules cover
     Chromium; this is the fallback for other engines). */
  const prefetched = new Set();
  const prefetchNow = (anchor) => {
    const href = anchor.href.split('#')[0];
    const path = anchor.pathname;
    if (
      prefetched.has(href) ||
      anchor.getAttribute('href').startsWith('#') ||
      path.startsWith('/cart') ||
      path.startsWith('/checkout') ||
      path.startsWith('/account') ||
      path.startsWith('/admin')
    ) {
      return;
    }
    prefetched.add(href);
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = href;
    link.as = 'document';
    document.head.appendChild(link);
  };
  let dwellTimer = null;
  document.addEventListener(
    'mouseover',
    (event) => {
      const anchor = event.target.closest && event.target.closest('a[href]');
      if (!anchor || anchor.origin !== location.origin) return;
      clearTimeout(dwellTimer);
      dwellTimer = setTimeout(() => prefetchNow(anchor), 65);
      anchor.addEventListener('mouseout', () => clearTimeout(dwellTimer), { once: true });
    },
    { passive: true }
  );
  document.addEventListener(
    'touchstart',
    (event) => {
      const anchor = event.target.closest && event.target.closest('a[href]');
      if (anchor && anchor.origin === location.origin) prefetchNow(anchor);
    },
    { passive: true }
  );

  /* ---------- Favourites (wishlist, localStorage) ---------- */
  const ElzWish = {
    read() {
      try {
        return JSON.parse(localStorage.getItem('elzWishlist')) || [];
      } catch (e) {
        return [];
      }
    },
    write(list) {
      try {
        localStorage.setItem('elzWishlist', JSON.stringify(list));
      } catch (e) {}
      this.sync();
    },
    toggle(item) {
      const list = this.read();
      const index = list.findIndex((entry) => entry.handle === item.handle);
      if (index > -1) list.splice(index, 1);
      else list.push(item);
      this.write(list);
      return index === -1;
    },
    remove(handle) {
      this.write(this.read().filter((entry) => entry.handle !== handle));
    },
    sync() {
      const list = this.read();
      document.querySelectorAll('[data-elz-wish]').forEach((btn) => {
        btn.classList.toggle('elz-wish--on', list.some((entry) => entry.handle === btn.dataset.handle));
      });
      document.querySelectorAll('[data-elz-wish-count]').forEach((el) => {
        el.textContent = list.length;
        el.hidden = !list.length;
      });
    },
  };
  window.ElzWish = ElzWish;

  document.addEventListener('click', (event) => {
    const btn = event.target.closest && event.target.closest('[data-elz-wish]');
    if (!btn) return;
    event.preventDefault();
    const added = ElzWish.toggle({
      handle: btn.dataset.handle,
      title: btn.dataset.title,
      url: btn.dataset.url,
      image: btn.dataset.image,
      price: parseInt(btn.dataset.price, 10) || 0,
    });
    ElzToast.show(added ? 'Added to favourites' : 'Removed from favourites', {
      link: '/pages/wishlist',
      linkLabel: 'View favourites',
    });
  });
  document.addEventListener('DOMContentLoaded', () => ElzWish.sync());
  document.addEventListener('shopify:section:load', () => ElzWish.sync());

  /* ---------- Footer tray: publish footer height for the sticky offset ---- */
  const initFootTray = () => {
    const foot = document.querySelector('.elz-footer');
    const sec = foot && foot.closest('.shopify-section');
    if (!sec) return;
    const setH = () => sec.style.setProperty('--elz-foot-h', foot.offsetHeight + 'px');
    if ('ResizeObserver' in window) new ResizeObserver(setH).observe(foot);
    setH();
  };
  document.addEventListener('DOMContentLoaded', initFootTray);
  document.addEventListener('shopify:section:load', initFootTray);

  /* ---------- Stale-count guard ----------
     Prefetched and prerendered pages carry the cart pill from whenever
     their HTML was generated, and bfcache restores freeze it too. On
     every view, check the pill against the live cart (one small JSON
     call) and re-render it only when they disagree. */
  const syncCartBubble = async () => {
    try {
      const holder = document.getElementById('cart-icon-bubble');
      if (!holder) return;
      const res = await fetch('/cart.js');
      const cart = await res.json();
      const shown = parseInt((holder.textContent.match(/\d+/) || ['0'])[0], 10);
      if (shown !== cart.item_count) QuickView.refreshCartBubble();
    } catch (e) {}
  };
  if (document.prerendering) {
    document.addEventListener('prerenderingchange', () => syncCartBubble(), { once: true });
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => syncCartBubble());
  } else {
    syncCartBubble();
  }
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      syncCartBubble();
      ElzWish.sync();
      // Buttons disabled right before a checkout navigation come back
      // disabled when the page is restored from the back/forward cache.
      document
        .querySelectorAll('[data-elz-cardpay], [data-elz-codpay], [data-elz-dchange], [data-elz-dadd]')
        .forEach((btn) => (btn.disabled = false));
      const drawer = document.querySelector('elz-cart-drawer');
      if (drawer && !drawer.hidden && drawer.refresh) drawer.refresh();
    }
  });

  /* ---------- Cart line variant swap (size selects in cart + drawer) ---- */
  const elzRefreshCartPage = async () => {
    const targets = ['main-cart-items', 'main-cart-footer']
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (!targets.length) return;
    try {
      const ids = targets.map((el) => el.dataset.id).join(',');
      const res = await fetch(`/?sections=${ids}`);
      const sections = await res.json();
      targets.forEach((el) => {
        const html = sections[el.dataset.id];
        if (!html) return;
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.js-contents');
        const dest = el.querySelector('.js-contents');
        if (fresh && dest) dest.innerHTML = fresh.innerHTML;
      });
    } catch (e) {}
  };
  document.addEventListener('change', async (event) => {
    const sel = event.target.closest && event.target.closest('[data-elz-swapvariant]');
    if (!sel) return;
    const newId = parseInt(sel.value, 10);
    const key = sel.dataset.key;
    const qty = parseInt(sel.dataset.qty, 10) || 1;
    sel.disabled = true;
    try {
      const addRes = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`.replace('.js.js', '.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: newId, quantity: qty }),
      });
      if (!addRes.ok) throw new Error('unavailable');
      await fetch(`${window.routes?.cart_change_url || '/cart/change'}.js`.replace('.js.js', '.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
    } catch (e) {
      sel.disabled = false;
      ElzToast.show('That option is unavailable right now');
    }
    const drawer = document.querySelector('elz-cart-drawer');
    if (drawer && !drawer.hidden) await drawer.refresh();
    QuickView.refreshCartBubble();
    elzRefreshCartPage();
  });

  /* ---------- Cart discount game (memory match) ----------
     Solve the 3-pair match -> discount code is applied and a countdown
     starts; when it runs out the offer hides for the rest of the day.
     Lives inside the cart summary's .js-contents, so it re-mounts after
     every cart AJAX re-render and restores its state from localStorage. */
  class ElzCartGame extends HTMLElement {
    connectedCallback() {
      this.code = this.dataset.code || 'PUZZLE1';
      this.minutes = parseInt(this.dataset.minutes, 10) || 3;
      this.percent = this.dataset.percent || '1';
      this.renderState();
    }
    disconnectedCallback() {
      clearInterval(this.clockTimer);
    }
    readState() {
      try {
        const state = JSON.parse(localStorage.getItem('elzCartGame')) || {};
        return state.day === new Date().toDateString() ? state : {};
      } catch (e) {
        return {};
      }
    }
    writeState(patch) {
      const state = { ...this.readState(), ...patch, day: new Date().toDateString() };
      try {
        localStorage.setItem('elzCartGame', JSON.stringify(state));
      } catch (e) {}
      return state;
    }
    deadline(state) {
      return (state.wonAt || 0) + this.minutes * 60000;
    }
    renderState() {
      clearInterval(this.clockTimer);
      const state = this.readState();
      if (state.status === 'expired') {
        this.hidden = true;
        return;
      }
      if (state.status === 'won') {
        if (Date.now() < this.deadline(state)) return this.renderWon(state);
        this.writeState({ status: 'expired' });
        this.hidden = true;
        setTimeout(() => elzDropCartGameCode(), 0);
        return;
      }
      this.renderGame();
    }
    renderGame() {
      this.hidden = false;
      const icons = ['🕷️', '🔥', '🧢'];
      const deck = icons.concat(icons).sort(() => Math.random() - 0.5);
      this.innerHTML = `
        <div class="elz-game__head">
          <span class="elz-game__title">Match the pairs, save ${this.percent}%</span>
          <span class="elz-game__sub">Solve it and a ${this.minutes}-minute code unlocks</span>
        </div>
        <div class="elz-game__grid"></div>`;
      const grid = this.querySelector('.elz-game__grid');
      let openCard = null;
      let locked = false;
      let matched = 0;
      deck.forEach((icon) => {
        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'elz-game__card';
        card.setAttribute('aria-label', 'Flip card');
        card.innerHTML = `<span class="elz-game__front" aria-hidden="true">?</span><span class="elz-game__back">${icon}</span>`;
        card.addEventListener('click', () => {
          if (locked || card.classList.contains('is-flip')) return;
          card.classList.add('is-flip');
          if (!openCard) {
            openCard = card;
            return;
          }
          const first = openCard;
          openCard = null;
          if (first.querySelector('.elz-game__back').textContent === icon) {
            first.classList.add('is-match');
            card.classList.add('is-match');
            matched += 1;
            if (matched === 3) setTimeout(() => this.win(), 500);
          } else {
            locked = true;
            setTimeout(() => {
              first.classList.remove('is-flip');
              card.classList.remove('is-flip');
              locked = false;
            }, 750);
          }
        });
        grid.appendChild(card);
      });
    }
    win() {
      const state = this.writeState({ status: 'won', wonAt: Date.now() });
      this.renderWon(state);
    }
    renderWon(state) {
      this.hidden = false;
      // Applying the code = hitting the discount URL, Shopify sets the
      // cart cookie and checkout picks it up automatically.
      fetch('/discount/' + encodeURIComponent(this.code)).catch(() => {});
      this.innerHTML = `
        <div class="elz-game__won">
          <div class="elz-game__wonline">✓ ${this.percent}% off unlocked, code <strong>${this.code}</strong> is applied at checkout</div>
          <div class="elz-game__clockrow">
            <span class="elz-game__clocklabel">Expires in</span>
            <span class="elz-game__clock" data-elz-clock>${this.minutes}:00</span>
          </div>
        </div>`;
      const clock = this.querySelector('[data-elz-clock]');
      const tick = () => {
        const left = this.deadline(state) - Date.now();
        if (left <= 0) {
          clearInterval(this.clockTimer);
          this.writeState({ status: 'expired' });
          this.hidden = true;
          setTimeout(() => elzDropCartGameCode(), 0);
          return;
        }
        const mins = Math.floor(left / 60000);
        const secs = Math.floor((left % 60000) / 1000);
        clock.textContent = mins + ':' + String(secs).padStart(2, '0');
      };
      tick();
      this.clockTimer = setInterval(tick, 1000);
    }
  }
  if (!customElements.get('elz-cart-game')) customElements.define('elz-cart-game', ElzCartGame);

  /* While a game win is active, route both checkout paths through
     /checkout?discount=<code> — the cookie set by /discount/ is ignored
     when a checkout session already exists, the URL param is not.
     The card button's 5% is the Scala app discount, triggered by the
     _scaladiscount cart attribute its own button sets; we replicate that
     and stack the game code on top (both are combinable order discounts). */
  const elzGameActiveCode = () => {
    try {
      const state = JSON.parse(localStorage.getItem('elzCartGame')) || {};
      if (state.day !== new Date().toDateString() || state.status !== 'won') return null;
      const el = document.querySelector('elz-cart-game');
      const minutes = el ? parseInt(el.dataset.minutes, 10) || 3 : 3;
      if (Date.now() >= (state.wonAt || 0) + minutes * 60000) return null;
      return (el && el.dataset.code) || 'PUZZLE1';
    } catch (e) {
      return null;
    }
  };
  /* The cart game's code only lasts its few minutes. It was applied through
     /discount/ (which also sets a cookie checkout reads), so on expiry it has
     to come off the cart too, or the shopper keeps the % for good. Also run
     on every page view: any cart still carrying the code without a live win
     (e.g. won on an earlier day) is cleaned up. */
  const elzCartGameCode = () => {
    const el = document.querySelector('elz-cart-game');
    return ((el && el.dataset.code) || 'PUZZLE1').toUpperCase();
  };
  const elzDropCartGameCode = async (cart) => {
    try {
      const code = elzCartGameCode();
      if ((elzGameActiveCode() || '').toUpperCase() === code) return false;
      const current = cart || (await (await fetch('/cart.js')).json());
      const codes = (current.discount_codes || []).map((d) => d.code);
      if (!codes.some((c) => c.toUpperCase() === code)) return false;
      await fetch(`${window.routes?.cart_update_url || '/cart/update'}.js`.replace('.js.js', '.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: codes.filter((c) => c.toUpperCase() !== code).join(',') }),
      });
      document.cookie = 'discount_code=; Max-Age=0; path=/';
      const drawer = document.querySelector('elz-cart-drawer');
      if (drawer && !drawer.hidden) await drawer.refresh();
      elzRefreshCartPage();
      return true;
    } catch (e) {
      return false;
    }
  };
  const elzCheckStaleGameCode = () => setTimeout(() => elzDropCartGameCode(), 1200);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', elzCheckStaleGameCode, { once: true });
  else elzCheckStaleGameCode();

  /* COD checkout opens the Releasit COD form for the whole cart instead of
     Shopify checkout. Same calls as Releasit's own cart button (form mode
     'normalCart' + form.open()), so the form lists the cart lines with their
     properties (Fast delivery) and applies automatic discounts (Hoodie Combo)
     itself, and Releasit empties the cart once the order is placed. A won
     game code is not read from the cart, so it is typed into the form's
     discount field (Releasit allows one discount per order, so it can refuse
     it next to an automatic one). Returns false when the theme's "Use the
     Releasit COD form" setting is off or the app isn't on the page; callers
     then go to Shopify checkout (COD is offered there too). */
  const elzOpenCodForm = (code) => {
    if (!window.elzCodApp) return false;
    const rsi = window._rsi;
    if (!rsi || !rsi.form || typeof rsi.form.open !== 'function') return false;
    try {
      rsi.form.mode = 'normalCart';
      if (rsi.form.deferLoading) rsi.form.open(false, false, false, false, 'cart=true');
      else rsi.form.open();
    } catch (e) {
      return false;
    }
    // the card 5% (Scala app, cart attribute) never rides along with COD
    fetch(`${window.routes?.cart_update_url || '/cart/update'}.js`.replace('.js.js', '.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { _scaladiscount: '' } }),
    }).catch(() => {});
    if (code) {
      const started = Date.now();
      // wait for the form itself to show before typing the code in
      const applyCode = () => {
        const modal = document.getElementById('_rsi-cod-form-modal');
        const input = document.getElementById('_rsi-modal-discounts-form-code');
        const apply = document.getElementById('_rsi-modal-discounts-form-apply');
        if (modal && input && apply && getComputedStyle(modal).display !== 'none') {
          input.value = code;
          input.dispatchEvent(new Event('input', { bubbles: true }));
          apply.click();
        } else if (Date.now() - started < 8000) {
          setTimeout(applyCode, 250);
        }
      };
      applyCode();
    }
    return true;
  };
  document.addEventListener(
    'click',
    (event) => {
      const closest = (sel) => event.target.closest && event.target.closest(sel);
      const codBtn = closest('button[name="checkout"]');
      const cardBtn = closest('scala-cart-button');
      if (!codBtn && !cardBtn) return;
      const code = elzGameActiveCode();
      if (codBtn && elzOpenCodForm(code)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      // the card button (Scala) handles itself unless a game code rides along;
      // COD to Shopify checkout always clears the card 5% attribute first
      if (cardBtn && !code) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();
      const cardFlow = !!cardBtn;
      const params = [];
      if (code) params.push(`discount=${encodeURIComponent(code)}`);
      if (cardFlow) params.push('skip_shop_pay=true');
      const target = '/checkout' + (params.length ? `?${params.join('&')}` : '');
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { _scaladiscount: cardFlow ? '1' : '' } }),
      })
        .catch(() => {})
        .finally(() => {
          window.location.href = target;
        });
    },
    true
  );

  /* ---------- Review card modal ---------- */
  const ReviewModal = {
    overlay: null,
    open(data) {
      this.close();
      this.lastFocus = document.activeElement;
      const overlay = document.createElement('div');
      overlay.className = 'elz-rvmodal';
      overlay.innerHTML = `
        <div class="elz-rvmodal__backdrop" data-elz-rv-close></div>
        <div class="elz-rvmodal__panel" role="dialog" aria-modal="true" aria-label="Customer review">
          <button type="button" class="elz-rvmodal__close" data-elz-rv-close aria-label="Close">✕</button>
          <div class="elz-rvmodal__media"${data.image ? '' : ' hidden'}></div>
          <div class="elz-rvmodal__body">
            <div class="elz-rvmodal__stars" aria-hidden="true">★★★★★</div>
            <p class="elz-rvmodal__text"></p>
            <div class="elz-rvmodal__foot">
              <span class="elz-rvmodal__name"></span>
              <span class="elz-rvmodal__verified">✓ ${data.verified || 'Verified'}</span>
            </div>
          </div>
        </div>`;
      if (data.image) {
        const img = document.createElement('img');
        img.src = data.image;
        img.alt = data.name || '';
        overlay.querySelector('.elz-rvmodal__media').appendChild(img);
      } else {
        overlay.querySelector('.elz-rvmodal__panel').classList.add('elz-rvmodal__panel--noimg');
      }
      overlay.querySelector('.elz-rvmodal__text').textContent = data.text || '';
      overlay.querySelector('.elz-rvmodal__name').textContent = data.name || '';
      overlay.addEventListener('click', (event) => {
        if (event.target.closest('[data-elz-rv-close]')) this.close();
      });
      this.onKey = (event) => {
        if (event.key === 'Escape') this.close();
      };
      document.addEventListener('keydown', this.onKey);
      document.body.appendChild(overlay);
      this.overlay = overlay;
      overlay.querySelector('.elz-rvmodal__close').focus();
    },
    close() {
      if (this.onKey) document.removeEventListener('keydown', this.onKey);
      if (this.overlay) {
        this.overlay.remove();
        this.overlay = null;
        if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
      }
    },
  };

  document.addEventListener('click', (event) => {
    const card = event.target.closest && event.target.closest('[data-elz-review]');
    if (card) ReviewModal.open(card.dataset);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const card = event.target.closest && event.target.closest('[data-elz-review]');
    if (card) {
      event.preventDefault();
      ReviewModal.open(card.dataset);
    }
  });

  /* ---------- Quick view ---------- */
  const elzMoney = (cents) => {
    const format = window.ElzMoneyFormat || 'Rs.[amount]';
    const amount = (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 });
    return format.replace(/\{\{\s*amount[^}]*\}\}/, amount).replace('[amount]', amount);
  };

  const QuickView = {
    overlay: null,
    lastFocus: null,

    async open(handle, url) {
      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) return (window.location.href = url);
        this.render(await res.json(), url);
      } catch (e) {
        window.location.href = url;
      }
    },

    render(product, url) {
      this.close();
      this.lastFocus = document.activeElement;
      const variants = product.variants || [];
      let current = variants.find((v) => v.available) || variants[0];

      const overlay = document.createElement('div');
      overlay.className = 'elz-qv-overlay';
      overlay.innerHTML = `
        <div class="elz-qv-modal" role="dialog" aria-modal="true" aria-label="${product.title}">
          <div class="elz-qv-mediawrap">
            <div class="elz-qv-media elz-noscroll" data-qv-media></div>
            <div class="elz-qv-dots" data-qv-dots aria-hidden="true"></div>
          </div>
          <div class="elz-qv-body">
            <button type="button" class="elz-qv-close" data-qv-close aria-label="Close">✕</button>
            <h2 class="elz-qv-title"></h2>
            <div class="elz-qv-pricerow">
              <span class="elz-qv-price" data-qv-price></span>
              <span class="elz-qv-was" data-qv-was hidden></span>
              <span class="elz-qv-save" data-qv-save hidden></span>
            </div>
            <div data-qv-options></div>
            <div class="elz-qv-buyrow">
              <quantity-input class="elz-qty">
                <button class="quantity__button elz-qty__btn" name="minus" type="button"><span aria-hidden="true">−</span></button>
                <input class="elz-qty__input" type="number" min="1" value="1" aria-label="Quantity" data-qv-qty>
                <button class="quantity__button elz-qty__btn" name="plus" type="button"><span aria-hidden="true">+</span></button>
              </quantity-input>
              <button type="button" class="elz-btn elz-qv-add" data-qv-add><span>Add to cart +</span></button>
            </div>
            <a class="elz-qv-link" data-qv-link>View full details →</a>
          </div>
        </div>`;

      overlay.querySelector('.elz-qv-title').textContent = product.title;
      overlay.querySelector('[data-qv-link]').href = url;

      const media = overlay.querySelector('[data-qv-media]');
      const dotsWrap = overlay.querySelector('[data-qv-dots]');
      const sources = (product.images || []).slice(0, 12);
      sources.forEach((src, i) => {
        const img = document.createElement('img');
        img.src = src + (src.includes('?') ? '&' : '?') + 'width=900';
        img.alt = product.title;
        if (i > 0) img.loading = 'lazy';
        media.appendChild(img);
        const dot = document.createElement('span');
        dot.className = 'elz-qv-dot' + (i === 0 ? ' elz-qv-dot--on' : '');
        dotsWrap.appendChild(dot);
      });
      if (sources.length < 2) dotsWrap.hidden = true;
      const syncDots = () => {
        const idx = Math.round(media.scrollLeft / Math.max(1, media.clientWidth));
        dotsWrap
          .querySelectorAll('.elz-qv-dot')
          .forEach((d, i) => d.classList.toggle('elz-qv-dot--on', i === idx));
      };
      media.addEventListener('scroll', () => requestAnimationFrame(syncDots), { passive: true });
      const cleanSrc = (src) => (src || '').split('?')[0];
      const setImage = () => {
        const src = current && current.featured_image && current.featured_image.src;
        if (!src) return;
        const idx = sources.findIndex((candidate) => cleanSrc(candidate) === cleanSrc(src));
        if (idx > -1) media.scrollTo({ left: idx * media.clientWidth, behavior: 'smooth' });
      };

      const priceEl = overlay.querySelector('[data-qv-price]');
      const wasEl = overlay.querySelector('[data-qv-was]');
      const saveEl = overlay.querySelector('[data-qv-save]');
      const addBtn = overlay.querySelector('[data-qv-add]');
      const syncState = () => {
        priceEl.textContent = elzMoney(current.price);
        const onSale = current.compare_at_price && current.compare_at_price > current.price;
        wasEl.hidden = !onSale;
        saveEl.hidden = !onSale;
        if (onSale) {
          const saving = current.compare_at_price - current.price;
          wasEl.textContent = elzMoney(current.compare_at_price);
          saveEl.textContent = `Save ${elzMoney(saving)} · ${Math.round((saving * 100) / current.compare_at_price)}% off`;
        }
        addBtn.disabled = !current.available;
        addBtn.querySelector('span').textContent = current.available
          ? (window.variantStrings?.addToCart || 'Add to cart') + ' +'
          : window.variantStrings?.soldOut || 'Sold out';
        setImage();
      };

      // Option rows built from variants (works for 1–3 options)
      const optWrap = overlay.querySelector('[data-qv-options]');
      const optionNames = product.options || [];
      const selected = optionNames.map((_, i) => current[`option${i + 1}`]);
      if (!(optionNames.length === 1 && variants.length === 1)) {
        optionNames.forEach((name, idx) => {
          const values = [...new Set(variants.map((v) => v[`option${idx + 1}`]))];
          const label = document.createElement('div');
          label.className = 'elz-qv-optlabel';
          label.textContent = typeof name === 'string' ? name : name.name;
          const row = document.createElement('div');
          row.className = 'elz-qv-opts';
          values.forEach((value) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'elz-qv-opt' + (selected[idx] === value ? ' elz-qv-opt--on' : '');
            btn.textContent = value;
            btn.addEventListener('click', () => {
              selected[idx] = value;
              const match = variants.find((v) => selected.every((val, i) => v[`option${i + 1}`] === val));
              if (match) current = match;
              row.querySelectorAll('.elz-qv-opt').forEach((b) => b.classList.toggle('elz-qv-opt--on', b === btn));
              syncState();
            });
            row.appendChild(btn);
          });
          optWrap.appendChild(label);
          optWrap.appendChild(row);
        });
      }
      const firstLabel = optWrap.querySelector('.elz-qv-optlabel');
      if (firstLabel) {
        const sizeChart = document.createElement('a');
        sizeChart.className = 'elz-qv-sizechart';
        sizeChart.href = url;
        sizeChart.textContent = 'Size chart';
        firstLabel.appendChild(sizeChart);
      }
      syncState();

      addBtn.addEventListener('click', async () => {
        addBtn.disabled = true;
        const qty = parseInt(overlay.querySelector('[data-qv-qty]').value, 10) || 1;
        const drawer = document.querySelector('elz-cart-drawer');
        if (drawer) {
          const image = (current.featured_image && current.featured_image.src) || product.featured_image;
          this.close();
          drawer.openPending({
            title: product.title,
            image: image ? `${image}${image.includes('?') ? '&' : '?'}width=200` : '',
            variant: current.title === 'Default Title' ? '' : current.title,
          });
        }
        try {
          if (!drawer) {
            const res = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`.replace('.js.js', '.js'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: current.id, quantity: qty }),
            });
            if (!res.ok) throw new Error('add failed');
            this.refreshCartBubble();
            this.close();
            ElzToast.show('Item added to your cart');
            return;
          }
          const data = await drawer.cartWrite('cart_add_url', '/cart/add', { id: current.id, quantity: qty });
          if (!drawer.applySections(data.sections)) {
            await drawer.refresh();
            this.refreshCartBubble();
          }
        } catch (e) {
          if (drawer) drawer.cancelPending();
          addBtn.disabled = false;
          window.location.href = url;
        }
      });

      overlay.addEventListener('click', (e) => {
        if (e.target === overlay || e.target.closest('[data-qv-close]')) this.close();
      });
      this.onKey = (e) => e.key === 'Escape' && this.close();
      document.addEventListener('keydown', this.onKey);

      document.body.appendChild(overlay);
      this.overlay = overlay;
      overlay.querySelector('.elz-qv-close').focus();
    },

    applyBubble(sections) {
      const html = sections && sections['cart-icon-bubble'];
      const holder = document.getElementById('cart-icon-bubble');
      if (!html || !holder) return false;
      const section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
      if (!section) return false;
      holder.innerHTML = section.innerHTML;
      return true;
    },

    async refreshCartBubble() {
      try {
        const res = await fetch(`${window.routes?.root_url || '/'}?sections=cart-icon-bubble`);
        const sections = await res.json();
        const holder = document.getElementById('cart-icon-bubble');
        if (holder && sections['cart-icon-bubble']) {
          holder.innerHTML = new DOMParser()
            .parseFromString(sections['cart-icon-bubble'], 'text/html')
            .querySelector('.shopify-section').innerHTML;
        }
      } catch (e) {}
    },

    close() {
      if (this.onKey) document.removeEventListener('keydown', this.onKey);
      if (this.overlay) {
        this.overlay.remove();
        this.overlay = null;
        if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
      }
    },
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('[data-elz-quickview]');
    if (!btn) return;
    e.preventDefault();
    QuickView.open(btn.dataset.elzQuickview, btn.dataset.productUrl);
  });

  /* ---------- Cosmetic: COD app buy button ----------
     The Releasit embed pins border-radius:2px as inline !important, which CSS
     cannot override. Align it with the design's pill language. The durable fix
     is the app's own button-style setting (see docs/handoff.md). */
  const pillifyCodButton = () => {
    const btn = document.getElementById('_rsi-buy-now-button');
    if (!btn) return false;
    const apply = () => {
      if (btn.style.getPropertyValue('border-radius') !== '999px') {
        btn.style.setProperty('border-radius', '999px', 'important');
        btn.style.setProperty('overflow', 'hidden');
        btn.style.setProperty('border-width', '0', 'important');
        btn.style.setProperty('height', '54px', 'important');
        btn.style.setProperty('align-items', 'center', 'important');
        btn.style.setProperty('justify-content', 'center', 'important');
      }
    };
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(btn, { attributes: true, attributeFilter: ['style'] });
    return true;
  };
  /* ---------- COD form: delivery options + free-shipping bar ----------
     Above its free threshold Releasit lists its Rs.250 "Shipping Rate" next
     to "Free Shipping". The site promises free delivery there, so when a free
     option is offered the paid one is hidden and the free one selected; below
     the threshold Releasit only offers the paid rate, which then shows again
     (e.g. after removing an item inside the form). A bar like the cart
     drawer's shows how much more unlocks free delivery, computed the way
     Releasit decides it: its subtotal minus discounts against the threshold
     of its own free rate, so the bar and the delivery line always agree. */
  const codFreeThreshold = () => {
    const s = (window._rsi && window._rsi.s) || window._RSI_COD_FORM_SETTINGS;
    const rates = s && s.shippingRates && s.shippingRates.items_array;
    if (!Array.isArray(rates)) return 0;
    for (const rate of rates) {
      if (rate.price !== 0) continue;
      const min = (rate.condition || []).find((c) => c.type === 'if_total_greater_or_equal_than' && c.value > 0);
      if (min) return min.value;
    }
    return 0;
  };
  const codMoney = (cents) => {
    const format = (window._RSI_COD_FORM_MONEY_FORMAT || '{{amount}}').replace(/<[^>]*>/g, '');
    const amount = (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return format.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  };
  // "Rs.2,200.00" -> 220000 (the number itself, not the "." of "Rs.")
  const codCents = (text) => {
    const match = String(text || '').match(/\d[\d,]*(?:\.\d+)?/);
    return match ? Math.round(parseFloat(match[0].replace(/,/g, '')) * 100) : NaN;
  };
  const codLineCents = (form, key) => {
    const value = form.querySelector(`[data-checkout-line="${key}"] ._rsi-modal-checkout-line-value`);
    const cents = value ? codCents(value.textContent) : NaN;
    return Number.isNaN(cents) ? 0 : cents;
  };
  const renderCodShipBar = () => {
    const form = document.querySelector('#_rsi-cod-form-modal ._rsi-modal-form');
    const totals = form && form.querySelector('._rsi-build-block-totals-summary');
    const threshold = codFreeThreshold();
    let bar = document.querySelector('[data-elz-rsi-shipbar]');
    if (!totals || !threshold || !form.querySelector('[data-checkout-line="subtotal"]')) {
      if (bar) bar.remove();
      return;
    }
    if (!bar || bar.nextElementSibling !== totals) {
      if (bar) bar.remove();
      bar = document.createElement('div');
      bar.className = 'elz-rsi-shipbar';
      bar.setAttribute('data-elz-rsi-shipbar', '');
      bar.innerHTML =
        '<span class="elz-drawer__shipmsg" data-elz-rsi-shipmsg></span>' +
        '<span class="elz-drawer__track"><span class="elz-drawer__fill" data-elz-rsi-shipfill></span></span>';
      totals.parentElement.insertBefore(bar, totals);
    }
    const amount = codLineCents(form, 'subtotal') - codLineCents(form, 'discount');
    const free = amount >= threshold;
    // whole rupees, rounded up (a 1% discount would otherwise read "Rs.673.50")
    const remaining = Math.ceil((threshold - amount) / 100) * 100;
    const text = free ? '✓ Free shipping unlocked' : `Add ${codMoney(remaining)} more in items for free shipping`;
    const msg = bar.querySelector('[data-elz-rsi-shipmsg]');
    if (msg.textContent !== text) msg.textContent = text;
    const width = `${Math.max(0, Math.min(100, Math.floor((amount * 100) / threshold)))}%`;
    const fill = bar.querySelector('[data-elz-rsi-shipfill]');
    if (fill.style.width !== width) fill.style.width = width;
  };
  const tidyCodShipping = () => {
    const list = document.querySelector('#_rsi-cod-form-modal ._rsi-modal-shipping-rates');
    if (list) {
      const items = [...list.querySelectorAll('._rsi-modal-shipping-rates-item')];
      const isFree = (item) => {
        const price = item.querySelector('._rsi-modal-checkout-line-price');
        if (!price) return false;
        const money = price.querySelector('.money');
        return money ? codCents(money.textContent) === 0 : /free/i.test(price.textContent);
      };
      const free = items.find(isFree);
      items.forEach((item) => {
        const hide = !!free && item !== free;
        if ((item.style.display === 'none') !== hide) item.style.display = hide ? 'none' : '';
      });
      if (free && free.dataset.selected !== 'true') free.click();
    }
    renderCodShipBar();
  };
  // Releasit rebuilds its form when it opens, so watch the page, not the form
  // (only when the theme uses Releasit: the watcher sees every page change)
  let codTidyQueued = false;
  if (window.elzCodApp) {
    new MutationObserver(() => {
      if (codTidyQueued) return;
      codTidyQueued = true;
      requestAnimationFrame(() => {
        codTidyQueued = false;
        tidyCodShipping();
      });
    }).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-selected'] });
  }

  document.addEventListener('DOMContentLoaded', () => {
    if (window.elzCodApp && !pillifyCodButton()) {
      const watcher = new MutationObserver(() => {
        if (pillifyCodButton()) watcher.disconnect();
      });
      watcher.observe(document.documentElement, { childList: true, subtree: true });
      setTimeout(() => watcher.disconnect(), 15000);
    }
  });

  /* ---------- Cart drawer ----------
     Slide-in cart that opens on every add-to-cart. Content is fetched
     fresh from the elz-cart-drawer section (Section Rendering API), so
     it always shows the live cart. */
  class ElzCartDrawerEl extends HTMLElement {
    connectedCallback() {
      if (this.__bound) return;
      this.__bound = true;
      this.content = this.querySelector('[data-elz-drawer-content]');
      this.onKey = (event) => {
        if (event.key === 'Escape') this.close();
      };
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-elz-drawer-close]')) {
          const link = event.target.closest('a[data-elz-drawer-close]');
          this.close();
          if (link) return; // let the link navigate
          event.preventDefault();
          return;
        }
        const qtyBtn = event.target.closest('[data-elz-dchange]');
        if (qtyBtn) return this.changeLine(qtyBtn);
        const addBtn = event.target.closest('[data-elz-dadd]');
        if (addBtn) return this.addSuggestion(addBtn);
        if (event.target.closest('[data-elz-cardpay]')) return this.checkoutFlow(true);
        if (event.target.closest('[data-elz-codpay]')) return this.checkoutFlow(false);
      });
    }
    // Cart write + fresh drawer/pill HTML in one round trip (Section
    // Rendering via the Ajax cart API's `sections` parameter).
    async cartWrite(route, fallback, payload) {
      const res = await fetch(`${window.routes?.[route] || fallback}.js`.replace('.js.js', '.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...payload, sections: 'elz-cart-drawer,cart-icon-bubble', sections_url: location.pathname }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'cart error');
      return data;
    }
    applySections(sections) {
      const drawerOk = this.render(sections && sections['elz-cart-drawer']);
      if (!QuickView.applyBubble(sections)) QuickView.refreshCartBubble();
      return drawerOk;
    }
    async addSuggestion(btn) {
      btn.disabled = true;
      try {
        const data = await this.cartWrite('cart_add_url', '/cart/add', { id: parseInt(btn.dataset.elzDadd, 10), quantity: 1 });
        if (this.applySections(data.sections)) return;
      } catch (e) {}
      await this.refresh();
      QuickView.refreshCartBubble();
    }
    async checkoutFlow(cardFlow) {
      // Card = the Scala app's 5% (triggered by its cart attribute); COD
      // clears the attribute so the card discount never leaks onto COD.
      // An active game win rides along as ?discount= either way.
      const code = elzGameActiveCode();
      if (!cardFlow && elzOpenCodForm(code)) {
        this.close();
        return;
      }
      const payButtons = this.querySelectorAll('[data-elz-cardpay], [data-elz-codpay]');
      payButtons.forEach((b) => (b.disabled = true));
      // failsafe: if the checkout navigation is blocked for any reason,
      // give the buttons back rather than leaving the drawer dead
      setTimeout(() => payButtons.forEach((b) => (b.disabled = false)), 6000);
      try {
        await fetch(`${window.routes?.cart_update_url || '/cart/update'}.js`.replace('.js.js', '.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { _scaladiscount: cardFlow ? '1' : '' } }),
        });
      } catch (e) {}
      const params = [];
      if (cardFlow) params.push('skip_shop_pay=true');
      if (code) params.push(`discount=${encodeURIComponent(code)}`);
      window.location.href = '/checkout' + (params.length ? `?${params.join('&')}` : '');
    }
    show() {
      if (!this.hidden) return;
      this.hidden = false;
      document.documentElement.classList.add('elz-drawer-lock');
      document.addEventListener('keydown', this.onKey);
      this.lastFocus = document.activeElement;
    }
    focusClose() {
      const closeBtn = this.querySelector('.elz-drawer__close');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    }
    async open() {
      this.show();
      await this.refresh();
      this.focusClose();
    }
    // Add-to-cart tapped: open now, with the item being added, and let
    // the real contents replace it when the cart answers.
    openPending(preview) {
      this.pendingDismissed = false;
      this.classList.add('is-pending');
      this.content.replaceChildren(ElzCartDrawerEl.pendingView(preview || {}));
      this.show();
      this.focusClose();
      clearTimeout(this.pendingTimer);
      this.pendingTimer = setTimeout(() => {
        if (this.classList.contains('is-pending')) this.refresh();
      }, 8000);
    }
    cancelPending() {
      if (!this.classList.contains('is-pending')) return;
      this.classList.remove('is-pending');
      clearTimeout(this.pendingTimer);
      this.close();
    }
    static pendingView({ title, image, variant }) {
      const el = (tag, cls, text) => {
        const node = document.createElement(tag);
        if (cls) node.className = cls;
        if (text) node.textContent = text;
        return node;
      };
      const inner = el('div', 'elz-drawer__inner');
      const head = el('div', 'elz-drawer__head');
      const titleEl = el('span', 'elz-drawer__title', 'Cart');
      const close = el('button', 'elz-drawer__close');
      close.type = 'button';
      close.setAttribute('data-elz-drawer-close', '');
      close.setAttribute('aria-label', 'Close');
      close.innerHTML = '<span aria-hidden="true">✕</span>';
      head.append(titleEl, close);
      const items = el('div', 'elz-drawer__items');
      const row = el('div', 'elz-drawer__item elz-drawer__item--pending');
      const thumb = el('span', 'elz-drawer__thumb');
      if (image) {
        const img = el('img');
        img.src = image;
        img.alt = '';
        thumb.appendChild(img);
      }
      const info = el('div', 'elz-drawer__info');
      if (title) info.appendChild(el('span', 'elz-drawer__name', title));
      if (variant) info.appendChild(el('span', 'elz-drawer__variant', variant));
      info.appendChild(el('span', 'elz-drawer__pendingnote', 'Adding to your cart…'));
      row.append(thumb, info);
      items.append(row, el('span', 'elz-drawer__skel'), el('span', 'elz-drawer__skel elz-drawer__skel--short'));
      inner.append(head, items);
      inner.setAttribute('aria-busy', 'true');
      return inner;
    }
    render(html) {
      if (!html) return false;
      const section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
      if (!section) return false;
      // a quantity change re-renders the drawer; keep the shopper where they were
      const prev = this.content.querySelector('.elz-drawer__items');
      const top = prev && !this.classList.contains('is-pending') ? prev.scrollTop : 0;
      this.content.innerHTML = section.innerHTML;
      if (top) {
        const next = this.content.querySelector('.elz-drawer__items');
        if (next) next.scrollTop = top;
      }
      this.classList.remove('is-pending');
      clearTimeout(this.pendingTimer);
      return true;
    }
    close() {
      if (this.classList.contains('is-pending')) this.pendingDismissed = true;
      this.hidden = true;
      document.documentElement.classList.remove('elz-drawer-lock');
      document.removeEventListener('keydown', this.onKey);
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    }
    async refresh() {
      try {
        this.classList.add('is-loading');
        const res = await fetch('/?sections=elz-cart-drawer');
        const sections = await res.json();
        this.render(sections['elz-cart-drawer']);
      } catch (e) {
      } finally {
        this.classList.remove('is-loading');
      }
    }
    async changeLine(btn) {
      const line = parseInt(btn.dataset.line, 10);
      const quantity = Math.max(0, parseInt(btn.dataset.qty, 10) || 0);
      this.querySelectorAll('[data-elz-dchange]').forEach((b) => (b.disabled = true));
      this.classList.add('is-loading');
      try {
        const data = await this.cartWrite('cart_change_url', '/cart/change', { line, quantity });
        if (this.applySections(data.sections)) return;
      } catch (e) {
      } finally {
        this.classList.remove('is-loading');
      }
      await this.refresh();
      QuickView.refreshCartBubble();
    }
  }
  if (!customElements.get('elz-cart-drawer')) customElements.define('elz-cart-drawer', ElzCartDrawerEl);

  /* ---------- Dawn cart-notification, rewired ----------
     Adds from product forms land here (product-form.js contract). Off the
     cart page we refresh the header pill and open the drawer instead of
     the toast; on the cart page the page itself re-renders, so the small
     toast stays (a drawer duplicating the page would be noise). */
  customElements.whenDefined('cart-notification').then(() => {
    const proto = customElements.get('cart-notification').prototype;
    if (proto.__elzPatched) return;
    proto.__elzPatched = true;
    const originalOpen = proto.open;
    const originalRender = proto.renderContents;
    proto.open = function () {
      originalOpen.call(this);
      clearTimeout(this.__elzTimer);
      this.__elzTimer = setTimeout(() => this.close(), 2600);
    };
    const originalSections = proto.getSectionsToRender;
    // Off the cart page the drawer replaces the notification, so the add
    // request only needs the pill and the drawer (fewer sections = a
    // faster response).
    proto.getSectionsToRender = function () {
      if (!document.querySelector('elz-cart-drawer') || elzOnCartPage()) return originalSections.call(this);
      return [{ id: 'cart-icon-bubble' }, { id: 'elz-cart-drawer' }];
    };
    proto.renderContents = function (parsedState) {
      const drawer = document.querySelector('elz-cart-drawer');
      if (!drawer || elzOnCartPage()) return originalRender.call(this, parsedState);
      try {
        QuickView.applyBubble(parsedState.sections);
      } catch (e) {}
      const dismissed = drawer.pendingDismissed;
      drawer.pendingDismissed = false;
      if (drawer.render(parsedState.sections && parsedState.sections['elz-cart-drawer'])) {
        if (dismissed) return;
        drawer.show();
        drawer.focusClose();
      } else if (!dismissed) {
        drawer.open();
      }
    };
  });

  /* Product forms: open the drawer the moment Add to cart is tapped
     (capture runs before Dawn's submit handler); the add response fills
     it. A failed add (cartError) closes it again so the form's error
     message is what the shopper sees. */
  function elzOnCartPage() {
    const cartUrl = (window.routes?.cart_url || '/cart').replace(/\/+$/, '');
    return location.pathname.replace(/\/+$/, '').endsWith(cartUrl);
  }
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.closest('product-form')) return;
      if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;
      const drawer = document.querySelector('elz-cart-drawer');
      if (!drawer || elzOnCartPage()) return;
      const button = form.querySelector('[type="submit"]');
      if (button && (button.disabled || button.getAttribute('aria-disabled') === 'true')) return;
      const root = form.closest('elz-product') || document;
      const title = root.querySelector('.elz-pdp__title');
      const image = root.querySelector('[data-elz-main-img]:not([hidden])') || root.querySelector('[data-elz-main-img]');
      const size = root.querySelector('.elz-pdp__size--on');
      drawer.openPending({
        title: title ? title.textContent.trim() : '',
        image: image ? image.currentSrc || image.src || image.dataset.src || '' : '',
        variant: size ? size.textContent.trim() : '',
      });
    },
    true
  );
  document.addEventListener('DOMContentLoaded', () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartError, () => {
      const drawer = document.querySelector('elz-cart-drawer');
      if (drawer) drawer.cancelPending();
    });
  });
})();
